import { Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import QRCode from 'qrcode';
import { RecruitmentApiService } from '../../services/recruitment-api.service';
import { OrganizationLink, RecruitmentDashboardData } from '../models/recruitment.models';

@Component({
  selector: 'app-recruitment-dashboard',
  templateUrl: './recruitment-dashboard.component.html',
  styleUrls: ['./recruitment-dashboard.component.css'],
  standalone: false,
})
export class RecruitmentDashboardComponent implements OnInit {
  dashboard: RecruitmentDashboardData | null = null;
  loading = true;
  errorMessage = '';
  qrCodeDataUrl = '';
  updatingLink = false;

  constructor(private recruitmentApi: RecruitmentApiService, private snackBar: MatSnackBar) {}

  ngOnInit(): void {
    this.loadDashboard();
  }

  loadDashboard(): void {
    this.loading = true;
    this.errorMessage = '';
    this.recruitmentApi.getDashboard().subscribe({
      next: (dashboard) => {
        this.dashboard = dashboard;
        this.loading = false;
        this.createQrCode(dashboard.organizationLink);
      },
      error: () => {
        this.errorMessage = 'We could not load your recruitment workspace.';
        this.loading = false;
      },
    });
  }

  async copyLink(): Promise<void> {
    const url = this.dashboard?.organizationLink?.url;
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      this.snackBar.open('Organization link copied', 'Close', { duration: 2200 });
    } catch {
      this.snackBar.open('Copy failed. Select the link and copy it manually.', 'Close', { duration: 3000 });
    }
  }

  createLink(): void {
    this.updatingLink = true;
    this.recruitmentApi.createOrganizationLink().subscribe({
      next: (link) => {
        if (this.dashboard) this.dashboard = { ...this.dashboard, organizationLink: link };
        this.createQrCode(link);
        this.updatingLink = false;
      },
      error: () => {
        this.updatingLink = false;
        this.snackBar.open('Unable to create organization link', 'Dismiss', { duration: 3000 });
      },
    });
  }

  revokeLink(): void {
    if (!confirm('Revoke this organization link? Anyone using it will no longer be able to join.')) return;
    this.updatingLink = true;
    this.recruitmentApi.revokeOrganizationLink().subscribe({
      next: () => {
        if (this.dashboard?.organizationLink) {
          this.dashboard = {
            ...this.dashboard,
            organizationLink: { ...this.dashboard.organizationLink, revoked: true },
          };
        }
        this.updatingLink = false;
        this.snackBar.open('Organization link revoked', 'Close', { duration: 2200 });
      },
      error: () => {
        this.updatingLink = false;
        this.snackBar.open('Unable to revoke organization link', 'Dismiss', { duration: 3000 });
      },
    });
  }

  initials(firstName: string, lastName: string): string {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }

  trackByBenchmark(_index: number, item: { benchmarkId: string }): string {
    return item.benchmarkId;
  }

  trackByCandidate(_index: number, item: { userId: string }): string {
    return item.userId;
  }

  private async createQrCode(link: OrganizationLink | null): Promise<void> {
    if (!link || link.revoked) {
      this.qrCodeDataUrl = '';
      return;
    }
    try {
      this.qrCodeDataUrl = await QRCode.toDataURL(link.url, {
        width: 180,
        margin: 1,
        color: { dark: '#102a56', light: '#ffffff' },
      });
    } catch {
      this.qrCodeDataUrl = '';
    }
  }
}
